import { Grid } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { RateInputs, RateOutputs } from '../types/rates';
import { lookupRates } from '../data/rateService';
import RateInputForm from './RateInputForm';
import RateOutputDisplay from './RateOutputDisplay';

interface RateLookupToolProps {
  resetTrigger?: number;
}

const RateLookupTool: React.FC<RateLookupToolProps> = ({ resetTrigger }) => {
  const [rateOutputs, setRateOutputs] = useState<RateOutputs | null>(null);
  const [currentInputs, setCurrentInputs] = useState<RateInputs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDirtyMessage, setShowDirtyMessage] = useState(false);

  const handleFormSubmit = async (data: RateInputs) => {
    setLoading(true);
    setError(null);
    setShowDirtyMessage(false);
    
    try {
      if (!data.investmentDate) {
        throw new Error('Investment date is required');
      }
      
      const rates = await lookupRates(data);
      setRateOutputs(rates);
      setCurrentInputs(data);
      return true; // Indicate successful submission
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setRateOutputs(null);
      return false; // Indicate failed submission
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = () => {
    if (rateOutputs) {
      setRateOutputs(null);
      setShowDirtyMessage(true);
    }
  };

  // Reset outputs when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined) {
      setRateOutputs(null);
      setCurrentInputs(null);
      setError(null);
      setShowDirtyMessage(false);
    }
  }, [resetTrigger]);

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 6 }}>
        <RateInputForm onSubmit={handleFormSubmit} resetTrigger={resetTrigger} loading={loading} onFormChange={handleFormChange} />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <RateOutputDisplay 
          rateOutputs={rateOutputs}
          currentInputs={currentInputs}
          error={error}
          loading={loading}
          showDirtyMessage={showDirtyMessage}
        />
      </Grid>
    </Grid>
  );
};

export default RateLookupTool;
